import { SellerOrderRequestBody } from 'cart'
import { defaultCredentials } from '../aesInit'
import {
  InvoiceInfoResponse,
  LocationTransports,
  OrderResponse,
} from '../api/cart/interfaces'
import { embeddedConfigManager } from '../api/embedded/EmbeddedConfigManager'
import { EmbeddedCommunicationsManager } from '../api/embedded/EmbeddedCommunicationsManager'
import { CatalogIndexTracker } from '../api/metrics/CatalogIndexTracker'
import { IS_INTEGRATED_VERSION } from '../config/ConfigManager'
import { getServiceName } from '../helpers/getServiceName'
import { StoreInstances } from '../store/StoreInstancesContainer'
import {
  GetOrderHistoryFilters,
  OrderHistoryItem,
  OrderSearchCriteria,
  ResponseOrderHistory,
} from '../store/orderHistory/interfaces'
import { BaseServiceProvider } from './BaseServiceProvider'

class OrderServiceProvider extends BaseServiceProvider {
  constructor() {
    super(import.meta.env.VITE_API_BASE_URL, 'order', '1.2')
  }

  makeMultiSellerOrder = async (
    sellerOrderRequestBody: SellerOrderRequestBody
  ): Promise<OrderResponse | undefined> => {
    const resp = await this.post<OrderResponse>(
      'multivehicleorder',
      sellerOrderRequestBody,
      {
        params: {
          buyerId:
            IS_INTEGRATED_VERSION &&
            EmbeddedCommunicationsManager.integrationInfo.allowAnyBuyer
              ? embeddedConfigManager.getPartsBasketCreds().buyerId
              : undefined,
          catalogId: 111,
        },
        headers: {
          'Service-Name': getServiceName(),
          'Catalog-Index': CatalogIndexTracker.index,
          'Partner-Name': IS_INTEGRATED_VERSION
            ? embeddedConfigManager.getPartsBasketCreds().partnerId
            : defaultCredentials.partnerId,
          // ...config.headers,
        },
      }
    )
    return resp?.data
  }

  getTransports = async (): Promise<LocationTransports[]> => {
    try {
      const resp = await this.get<LocationTransports[]>('transports', {
        params: [],
      })
      return resp?.data
    } catch (_e) {
      return []
    }
  }

  getOrderHistory = async (
    orgId: string,
    start: number,
    limit: number,
    multiSeller: boolean,
    filters?: GetOrderHistoryFilters,
    sort?: 'orderedDate' | 'quantity' | 'totalCost'
  ): Promise<ResponseOrderHistory> => {
    const wdOrgId = StoreInstances.userStore.preferences?.wdorgId
    const treePath = StoreInstances.userStore.preferences?.treePath
    const orderSearchHistoryCriteria: OrderSearchCriteria = {
      orgId,
      wdOrgId,
      wdAdmin: false,
      treePath,
      searchTerm: filters?.searchTerm || '',
      poNumber: filters?.poNumber || '',
      confirmationNbr: filters?.confirmationNbr || '',
      location: filters?.location || '',
      partNumber: filters?.partNumber || '',
      noteTxt: filters?.noteTxt || '',
      vehicle: filters?.vehicle || {
        year: '',
        make: '',
        model: '',
        engine: '',
      },
      orderId: '',
      invoiceNbr: '',
      customerName: '',
      fromDate: filters?.fromDate ?? filters?.fromDate,
      toDate: filters?.toDate ?? filters?.toDate,
      start: start || 0,
      limit: limit || 10,
    }
    if (sort) {
      orderSearchHistoryCriteria.sortorder = { sortBy: sort, dir: 'desc' }
    }
    let resp
    const urlString = `order/history`
    const urlEncoded = encodeURI(urlString)
    try {
      resp = await this.post<ResponseOrderHistory>(
        urlEncoded,
        orderSearchHistoryCriteria,
        { params: { multiSeller } }
      )
      if (resp.status === 200) {
        const orderHistoryData: ResponseOrderHistory = {
          ...resp.data,
          orders: resp.data.orders?.map((order: OrderHistoryItem) => {
            return {
              ...order,
              header: {
                ...order.header,
                createdOn: order.isMyPlaceOrder
                  ? order.header?.createdOn
                  : order.ddsDetails[0]?.order?.orderDate,
                orderId: order.isMyPlaceOrder
                  ? order.header?.orderId
                  : order.ddsDetails[0]?.order?.uid,
                confirmationNbr: order.isMyPlaceOrder
                  ? order.header?.confirmationNbr
                  : order.ddsDetails[0]?.order?.confirmationNumber,
                totalCostPrice: order.isMyPlaceOrder
                  ? order.header?.totalCostPrice?.toFixed(2)
                  : order.ddsDetails[0]?.order?.totalCost?.toFixed(2),
                totalQty: order.isMyPlaceOrder
                  ? order.header?.totalQty
                  : order.ddsDetails[0]?.order?.totalCount,
                poNumber: order.isMyPlaceOrder
                  ? order.header?.poNumber
                  : order.ddsDetails[0]?.order?.purchaseOrderNumber,
              },
            }
          }),
        }
        return orderHistoryData
      }
      if (resp.status === 204) {
        return { totalCount: 0, orders: [] }
      }
    } catch (error) {
      throw new Error(error.toString())
    }
    return { totalCount: 0, orders: [] }
  }

  getOrderById = async (
    orderId: string,
    isMyPlaceOrder?: boolean
  ): Promise<OrderHistoryItem> => {
    const urlString = isMyPlaceOrder
      ? `order/${orderId}`
      : `/order/invoice/${orderId}`
    const urlEncoded = encodeURI(urlString)
    try {
      const resp = await this.get<OrderHistoryItem>(urlEncoded)
      if (resp.status === 200) {
        return resp.data
      }
    } catch (_error) {
      throw new Error('Order not found')
    }
    return {} as OrderHistoryItem
  }

  getInvoiceInfo = async (): Promise<InvoiceInfoResponse> => {
    const treePath = StoreInstances.userStore.preferences?.treePath
    const resp = await this.post<InvoiceInfoResponse>('/order/getInvoiceInfo', {
      treePath,
    })
    return resp.data
  }
}

export default new OrderServiceProvider()
